import clsx from 'clsx';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { TopBar } from '../../app/AppShell';
import { errorText } from '../../lib/api';
import { useLayout } from '../../lib/media';
import { useMe, useSettingsFull } from '../../lib/queries';
import { Icon } from '../../ui/icons';
import { EmptyState, Skeleton } from '../../ui/primitives';
import { DataSection } from './data';
import { HolidayModal, MembersSection } from './members';
import { AISection, GeneralSection, NotifySection, RulesSection, ShortcutsSection, VisibilitySection } from './sections';
import './settings.css';

// 设置（设计稿 14c、22c–e）：左侧分区导航，右侧内容。管理员可改，其他人只读。

type SectionId = 'general' | 'rules' | 'ai' | 'members' | 'visibility' | 'notify' | 'data' | 'shortcuts';

interface NavEntry {
  id: SectionId | 'profile';
  label: string;
  /** 谁能看到：all 所有人，manager 主管和管理员，admin 仅管理员 */
  who: 'all' | 'manager' | 'admin';
}

const WORKSPACE: NavEntry[] = [
  { id: 'general', label: '通用', who: 'all' },
  { id: 'rules', label: '日报与异常', who: 'all' },
  { id: 'ai', label: 'AI 追问', who: 'all' },
  { id: 'members', label: '团队与成员', who: 'manager' },
  { id: 'visibility', label: '可见范围', who: 'all' },
  { id: 'notify', label: '通知', who: 'all' },
  { id: 'data', label: '数据与日志', who: 'admin' },
];
const MINE: NavEntry[] = [
  { id: 'profile', label: '个人资料', who: 'all' },
  { id: 'shortcuts', label: '快捷键', who: 'all' },
];

export function SettingsPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'mobile';
  const { data: me } = useMe();
  const { data: dto, error } = useSettingsFull();
  const [holidays, setHolidays] = useState(false);

  const isAdmin = me?.role === 'admin';
  const allowed = (e: NavEntry) => e.who === 'all' || (e.who === 'manager' ? !!me?.isManager || isAdmin : isAdmin);
  const workspace = WORKSPACE.filter(allowed);
  const all = [...workspace, ...MINE];
  const current = all.find((e) => e.id === section);

  if (!mobile && !section) return <Navigate to="/settings/rules" replace />;
  if (section && (!current || current.id === 'profile')) return <Navigate to={section === 'profile' ? '/me' : '/settings'} replace />;

  const go = (e: NavEntry) => navigate(e.id === 'profile' ? '/me' : `/settings/${e.id}`);
  const nav = (
    <nav className={clsx('st-nav', mobile && 'mobile')} aria-label="设置">
      <div className="st-nav-group">工作区</div>
      {workspace.map((e) => (
        <button key={e.id} type="button" className={clsx('st-nav-item', e.id === section && 'on')} onClick={() => go(e)}>
          <span className="flex1">{e.label}</span>
          {mobile && <Icon name="chevR" size={14} />}
        </button>
      ))}
      <div className="st-nav-group" style={{ marginTop: 16 }}>
        我的
      </div>
      {MINE.filter((e) => !(mobile && e.id === 'shortcuts')).map((e) => (
        <button key={e.id} type="button" className={clsx('st-nav-item', e.id === section && 'on')} onClick={() => go(e)}>
          <span className="flex1">{e.label}</span>
          {(mobile || e.id === 'profile') && <Icon name="chevR" size={14} />}
        </button>
      ))}
    </nav>
  );

  let content;
  if (error) content = <EmptyState icon="wifioff" title="设置没有加载出来" desc={errorText(error)} />;
  else if (!dto) content = <Skeleton w={220} h={22} />;
  else
    switch (current?.id) {
      case 'general':
        content = <GeneralSection dto={dto} />;
        break;
      case 'rules':
        content = <RulesSection dto={dto} onHolidays={() => setHolidays(true)} />;
        break;
      case 'ai':
        content = <AISection dto={dto} />;
        break;
      case 'members':
        content = <MembersSection dto={dto} isAdmin={isAdmin} onHolidays={() => setHolidays(true)} />;
        break;
      case 'visibility':
        content = <VisibilitySection dto={dto} />;
        break;
      case 'notify':
        content = <NotifySection dto={dto} />;
        break;
      case 'data':
        content = <DataSection dto={dto} />;
        break;
      case 'shortcuts':
        content = <ShortcutsSection />;
        break;
    }

  if (mobile) {
    return (
      <div className="st-m">
        {section ? (
          <>
            <button type="button" className="st-m-back" onClick={() => navigate('/settings')}>
              <Icon name="chevL" size={18} color="var(--text-3)" />
              <span>设置</span>
            </button>
            <div className="st-m-body">{content}</div>
          </>
        ) : (
          <div className="st-m-body">
            <h1 className="t-page-title" style={{ marginBottom: 12 }}>
              设置
            </h1>
            {nav}
          </div>
        )}
        {holidays && dto && <HolidayModal dto={dto} onClose={() => setHolidays(false)} />}
      </div>
    );
  }

  return (
    <>
      <TopBar crumbs={['设置', current?.label ?? '']} />
      <div className="st">
        {nav}
        <div className="st-body">
          <div className="st-in">{content}</div>
        </div>
      </div>
      {holidays && dto && <HolidayModal dto={dto} onClose={() => setHolidays(false)} />}
    </>
  );
}
